import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { convertIdsToDisplay, replaceFileRefsWithNames } from '../../../../../utils/automation/variableUtils';
import { isRecordedPlanContext, isSavedRecordedPlanIdleView } from '../../../../AutomationStudio/utils/replayEligibility';
import InstructionItem from './InstructionItem';
import StepContextMenu from './StepContextMenu';
import styles from '../../../../../styles/automationStudio/automationStudio.module.scss';
import {
    getActiveInstructionIdFromFlow,
    getActiveInstructionsInPlanOrder,
    findInstructionIdForFlatStepIndex,
    computeReplayHighlightFlatIndex,
    computeGenerationActiveFlatIndex,
    buildPlanFlatStartByInstructionId,
    captureGenerationBaselineIds,
    hasInstructionsAddedAfterBaseline,
} from './instructionHierarchyUtils';
import { getActiveFlatSteps } from '../../../../../features/automationStudioNew/planUtils';

export default function InstructionHierarchy({
    instructions = [],
    selectedStep = null,
    selectedStepIds = [],
    isRunning = false,
    isGenerating = false,
    isReplayExecuting = false,
    isReplayFlow = false,
    isLoadedFromTestcase = false,
    usesLeftPanelGroupSnapshot = false,
    hasExecutedInSession = false,
    replayProgressMap = {},
    replayExecutionResults = null,
    replayCurrentStepIndex = null,
    executionFlow = null,
    fileSuggestions = [],
    variablesList = [],
    variablesListDescriptions = {},
    canCopy = false,
    canPaste = false,
    onInstructionSelect,
    onStepSelect,
    onAddStep,
    onStepIdSelect,
    onCopy,
    onPasteAbove,
    onPasteBelow,
    onContextMenuOpen,
    instructionHeadingsSnapshot = {},
    stepValidationErrors = {},
}) {
    const isRecordedPlan = useMemo(() => isRecordedPlanContext(instructions), [instructions]);
    const isSavedPlanIdle = useMemo(
        () => isSavedRecordedPlanIdleView({
            instructions,
            hasExecutedInSession,
            isRunning,
            isReplayExecuting,
            isGenerating,
        }),
        [instructions, hasExecutedInSession, isRunning, isReplayExecuting, isGenerating]
    );
    const isLiveRunActive = isRunning && !isReplayExecuting;
    const useLoadedSnapshotGrouping = isLoadedFromTestcase || usesLeftPanelGroupSnapshot;

    useEffect(() => {
        if (!hasExecutedInSession && useLoadedSnapshotGrouping) {
            setGenerationStatuses({});
            hadActiveGenerationSessionRef.current = false;
            postGenerationBaselineIdsRef.current = null;
            prevInProgressIdRef.current = null;
        }
    }, [hasExecutedInSession, useLoadedSnapshotGrouping]);

    const [menuState, setMenuState] = useState(null);
    const [selectedInstructionId, setSelectedInstructionId] = useState(null);
    const [selectedStepId, setSelectedStepId] = useState(null);
    const [expandedInstructions, setExpandedInstructions] = useState({});
    const [generationStatuses, setGenerationStatuses] = useState({});

    const prevInProgressIdRef = useRef(null);
    const hadActiveGenerationSessionRef = useRef(false);
    const postGenerationBaselineIdsRef = useRef(null);
    const activeExecInstructionRef = useRef(null);
    const hierarchyRootRef = useRef(null);
    const prevReplayExecutingRef = useRef(false);

    const allValidInstructions = useMemo(
        () => instructions.filter((inst) => !inst.removedFromEditor && inst.text?.trim()),
        [instructions]
    );

    useEffect(() => {
        if (isGenerating) {
            hadActiveGenerationSessionRef.current = true;
            postGenerationBaselineIdsRef.current = null;
            const ids = allValidInstructions.map((inst) => inst.id);
            const initial = {};
            if (ids.length > 0) {
                initial[ids[0]] = 'inprogress';
                prevInProgressIdRef.current = ids[0];
                setExpandedInstructions((prev) => ({ ...prev, [ids[0]]: true }));
            }
            setGenerationStatuses(initial);
        } else {
            if (!hadActiveGenerationSessionRef.current) return;
            postGenerationBaselineIdsRef.current = captureGenerationBaselineIds(instructions);
            hadActiveGenerationSessionRef.current = false;
            const lastInst = allValidInstructions[allValidInstructions.length - 1];
            const toCollapse = [];
            setGenerationStatuses((prev) => {
                const next = { ...prev };
                let changed = false;
                allValidInstructions.forEach((inst) => {
                    if (next[inst.id] === 'inprogress' || !next[inst.id]) {
                        const hasSteps = inst.aiSteps && inst.aiSteps.length > 0;
                        next[inst.id] = hasSteps ? null : 'skipped';
                        changed = true;
                        if (lastInst == null || String(inst.id) !== String(lastInst.id)) {
                            toCollapse.push(inst.id);
                        }
                    }
                });
                return changed ? next : prev;
            });
            if (toCollapse.length > 0) {
                setExpandedInstructions((prev) => {
                    const next = { ...prev };
                    toCollapse.forEach((id) => { next[id] = false; });
                    return next;
                });
            }
            prevInProgressIdRef.current = null;
        }
    }, [isGenerating, allValidInstructions, instructions]);

    // After generation, adding a new instruction should not reuse post-run left-panel chrome.
    useEffect(() => {
        if (isGenerating || !postGenerationBaselineIdsRef.current?.size) return;
        if (!hasInstructionsAddedAfterBaseline(instructions, postGenerationBaselineIdsRef.current)) {
            return;
        }
        if (Object.keys(generationStatuses).length > 0) {
            setGenerationStatuses({});
        }
    }, [instructions, isGenerating, generationStatuses]);

    useEffect(() => {
        if (!isGenerating || isReplayFlow) return;
        const firstId = allValidInstructions[0]?.id;
        if (firstId == null) return;
        const activeId = getActiveInstructionIdFromFlow(executionFlow, firstId);
        if (activeId == null) return;
        const prevId = prevInProgressIdRef.current;
        const sameId = (a, b) => a != null && b != null && String(a) === String(b);
        if (sameId(prevId, activeId)) return;

        const toCollapse = [];
        setGenerationStatuses((prev) => {
            const next = { ...prev };
            if (prevId != null && !sameId(prevId, activeId)) {
                const oldInst = allValidInstructions.find((inst) => String(inst.id) === String(prevId));
                next[prevId] = oldInst?.aiSteps?.length > 0 ? null : 'skipped';
                toCollapse.push(prevId);
                const oldIdx = allValidInstructions.findIndex((inst) => String(inst.id) === String(prevId));
                const newIdx = allValidInstructions.findIndex((inst) => String(inst.id) === String(activeId));
                if (oldIdx >= 0 && newIdx >= 0) {
                    for (let i = oldIdx + 1; i < newIdx; i += 1) {
                        const mid = allValidInstructions[i];
                        if (next[mid.id] === 'inprogress' || !next[mid.id]) {
                            next[mid.id] = mid.aiSteps?.length > 0 ? null : 'skipped';
                            toCollapse.push(mid.id);
                        }
                    }
                }
            }
            next[activeId] = 'inprogress';
            return next;
        });
        setExpandedInstructions((prev) => {
            const next = { ...prev };
            toCollapse.forEach((id) => { next[String(id)] = false; });
            next[String(activeId)] = true;
            return next;
        });
        prevInProgressIdRef.current = activeId;
    }, [isGenerating, isReplayFlow, executionFlow, allValidInstructions]);

    const displayInstructions = useMemo(() => {
        if (isGenerating) return allValidInstructions;
        const hasStatuses = Object.keys(generationStatuses).length > 0;
        if (hasStatuses) {
            return instructions.filter(
                (inst) => inst.aiSteps?.length > 0 || (!inst.removedFromEditor && inst.text?.trim())
            );
        }
        if (useLoadedSnapshotGrouping) {
            return instructions.filter((inst) => !inst.removedFromEditor);
        }
        return instructions.filter((inst) => inst.aiSteps && inst.aiSteps.length > 0);
    }, [useLoadedSnapshotGrouping, isGenerating, instructions, allValidInstructions, generationStatuses]);

    const instructionsInPlanOrder = useMemo(
        () => getActiveInstructionsInPlanOrder(instructions),
        [instructions]
    );

    const flatStepsForReplay = useMemo(
        () => getActiveFlatSteps(instructions),
        [instructions]
    );

    const planFlatStartByInstructionId = useMemo(
        () => buildPlanFlatStartByInstructionId(instructionsInPlanOrder),
        [instructionsInPlanOrder]
    );

    const replayHighlightFlatIndex = useMemo(
        () => computeReplayHighlightFlatIndex({
            isReplayExecuting,
            instructionsInPlanOrder,
            replayCurrentStepIndex,
            replayProgressMap,
            replayExecutionResults,
        }),
        [isReplayExecuting, instructionsInPlanOrder, replayCurrentStepIndex, replayProgressMap, replayExecutionResults]
    );

    const generationActiveFlatIndex = useMemo(
        () => computeGenerationActiveFlatIndex(instructions, isGenerating && isRunning),
        [instructions, isGenerating, isRunning]
    );

    const instructionIds = useMemo(
        () => displayInstructions.map((i) => i.id).join(','),
        [displayInstructions]
    );

    useEffect(() => {
        setExpandedInstructions((prev) => {
            const updated = { ...prev };
            let hasChanges = false;
            displayInstructions.forEach((inst) => {
                if (!(inst.id in updated)) {
                    updated[inst.id] = true;
                    hasChanges = true;
                }
            });
            return hasChanges ? updated : prev;
        });
    }, [instructionIds]);

    useEffect(() => {
        if (!isReplayExecuting || replayHighlightFlatIndex == null) return;
        const totalFlat = instructionsInPlanOrder.reduce((acc, inst) => acc + (inst.aiSteps?.length || 0), 0);
        if (totalFlat === 0) return;
        const instructionIdForFlat = findInstructionIdForFlatStepIndex(instructionsInPlanOrder, replayHighlightFlatIndex);
        if (instructionIdForFlat == null) return;
        setExpandedInstructions(() => {
            const next = {};
            displayInstructions.forEach((inst) => {
                next[inst.id] = String(inst.id) === String(instructionIdForFlat);
            });
            return next;
        });
    }, [isReplayExecuting, replayHighlightFlatIndex, instructionsInPlanOrder, displayInstructions]);

    useEffect(() => {
        if (!isReplayExecuting || replayHighlightFlatIndex == null) return undefined;
        const idx = replayHighlightFlatIndex;
        const t = window.setTimeout(() => {
            const root = hierarchyRootRef.current;
            const el = root?.querySelector(`[data-replay-scroll-index="${idx}"]`);
            el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 0);
        return () => clearTimeout(t);
    }, [isReplayExecuting, replayHighlightFlatIndex]);

    useEffect(() => {
        if (isReplayExecuting || !isRunning || generationActiveFlatIndex == null) return undefined;
        const idx = generationActiveFlatIndex;
        const t = window.setTimeout(() => {
            const root = hierarchyRootRef.current;
            const el = root?.querySelector(`[data-replay-scroll-index="${idx}"]`);
            el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 0);
        return () => clearTimeout(t);
    }, [isReplayExecuting, isRunning, generationActiveFlatIndex]);

    useEffect(() => {
        if (prevReplayExecutingRef.current && !isReplayExecuting) {
            setExpandedInstructions(() => {
                const next = {};
                displayInstructions.forEach((inst) => { next[inst.id] = true; });
                return next;
            });
        }
        prevReplayExecutingRef.current = isReplayExecuting;
    }, [isReplayExecuting, displayInstructions]);

    useEffect(() => {
        if (selectedStep) {
            if (!isReplayExecuting) setSelectedStepId(selectedStep.id);
            if (isReplayExecuting) return;
            const instructionWithStep = displayInstructions.find((inst) =>
                inst.aiSteps?.some((s) => s.id === selectedStep.id)
            );
            if (instructionWithStep) {
                setExpandedInstructions((prev) => {
                    const next = { ...prev };
                    if ((isRunning || isReplayExecuting) && activeExecInstructionRef.current && activeExecInstructionRef.current !== instructionWithStep.id) {
                        next[activeExecInstructionRef.current] = false;
                    }
                    next[instructionWithStep.id] = true;
                    return next;
                });
                if (isRunning || isReplayExecuting) {
                    activeExecInstructionRef.current = instructionWithStep.id;
                }
            }
        } else {
            if (!isReplayExecuting) setSelectedStepId(null);
            if (!isRunning && !isReplayExecuting && activeExecInstructionRef.current) {
                setExpandedInstructions((prev) => ({ ...prev, [activeExecInstructionRef.current]: false }));
                activeExecInstructionRef.current = null;
            }
        }
    }, [selectedStep?.id, displayInstructions, isRunning, isReplayExecuting]);

    const handleInstructionSelect = useCallback((instructionId) => {
        setSelectedInstructionId(instructionId);
        onInstructionSelect?.(instructionId);
    }, [onInstructionSelect]);

    const handleStepSelect = useCallback((step, event) => {
        setSelectedStepId(step.id);
        onStepSelect?.(step, event);
    }, [onStepSelect]);

    const handleToggleExpand = useCallback((instructionId) => {
        setExpandedInstructions((prev) => ({
            ...prev,
            [instructionId]: !prev[instructionId],
        }));
    }, []);

    const handleStepRowContextMenu = useCallback((id) => (e) => {
        e.preventDefault();
        onStepIdSelect?.(id);
        onContextMenuOpen?.(id);
        setMenuState({ anchorPosition: { top: e.clientY, left: e.clientX }, stepId: id });
    }, [onStepIdSelect, onContextMenuOpen]);

    if (displayInstructions.length === 0) return null;

    return (
        <div ref={hierarchyRootRef} className={styles.instructionHierarchy}>
            <div className={styles.hierarchyContent}>
                {displayInstructions.map((instruction, index) => {
                    const groupFlatStart = planFlatStartByInstructionId.has(instruction.id)
                        ? planFlatStartByInstructionId.get(instruction.id)
                        : 0;
                    const headingKey = String(instruction.id);
                    const rawText = instructionHeadingsSnapshot[headingKey] ?? instruction.text?.trim() ?? '';
                    const withVariables = convertIdsToDisplay(rawText, variablesList, variablesListDescriptions);
                    const displayText = replaceFileRefsWithNames(withVariables, fileSuggestions);
                    return (
                        <InstructionItem
                            key={instruction.id}
                            instruction={instruction}
                            instructionNumber={index + 1}
                            displayText={displayText}
                            isSelected={selectedInstructionId === instruction.id}
                            selectedStepId={selectedStepId}
                            selectedStepIds={selectedStepIds}
                            isExpanded={expandedInstructions[instruction.id] || false}
                            onInstructionSelect={handleInstructionSelect}
                            onStepSelect={handleStepSelect}
                            onToggleExpand={handleToggleExpand}
                            isRunning={isRunning}
                            onAddStep={onAddStep}
                            instructionId={instruction.id}
                            replayProgressMap={replayProgressMap}
                            replayExecutionResults={replayExecutionResults}
                            flatStepIndexStart={groupFlatStart}
                            flatStepsForReplay={flatStepsForReplay}
                            isReplayExecuting={isReplayExecuting}
                            replayCurrentStepIndex={
                                replayHighlightFlatIndex != null
                                    ? replayHighlightFlatIndex
                                    : replayCurrentStepIndex
                            }
                            replayHighlightFlatIndex={replayHighlightFlatIndex}
                            generationActiveFlatIndex={generationActiveFlatIndex}
                            isLoadedFromTestcase={isLoadedFromTestcase}
                            hasExecutedInSession={hasExecutedInSession}
                            isReplayFlow={isReplayFlow}
                            isSavedPlanIdle={isSavedPlanIdle}
                            isLiveRunActive={isLiveRunActive}
                            allInstructions={instructions}
                            generationStatus={
                                useLoadedSnapshotGrouping && !(instruction.aiSteps?.length > 0)
                                    ? 'skipped'
                                    : (
                                        generationStatuses[instruction.id] ??
                                        generationStatuses[String(instruction.id)] ??
                                        null
                                    )
                            }
                            variablesList={variablesList}
                            variablesListDescriptions={variablesListDescriptions}
                            fileSuggestions={fileSuggestions}
                            stepValidationErrors={stepValidationErrors}
                            onStepIdSelect={onStepIdSelect}
                            onStepContextMenu={handleStepRowContextMenu}
                        />
                    );
                })}
            </div>
            {menuState && (
                <StepContextMenu
                    isOpen
                    anchorPosition={menuState.anchorPosition}
                    onClose={() => setMenuState(null)}
                    canCopy={canCopy}
                    canPaste={canPaste}
                    onCopy={() => onCopy?.(menuState.stepId)}
                    onPasteAbove={() => onPasteAbove?.(menuState.stepId)}
                    onPasteBelow={() => onPasteBelow?.(menuState.stepId)}
                />
            )}
        </div>
    );
}
